import { Component, inject, signal } from '@angular/core';
import { ApiEndpoints } from '../../../../../core/api-endpoints';
import { FlashService } from '../../../../../services/flash';
import { ClassService } from '../../../../../services/class';
import { IClass, IClasses } from '../../../../../interfaces/class';
import { ActionButton } from '../../../../../shared/action-button/action-button';
import { LoadingIndicator } from '../../../../../shared/loading-indicator/loading-indicator';
import { NotFoundError } from '../../../../../shared/not-found-error/not-found-error';
import { Pagination } from '../../../../../shared/pagination/pagination';
import { Helpers } from '../../../../../utilities/helpers';
import { NgClass } from '@angular/common';
import { ITeachers } from '../../../../../interfaces/teacher';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { SchoolService } from '../../../../../services/school';
import { Session } from '../../../../../services/session';
import { ISchools } from '../../../../../interfaces/school';
import { LevelService } from '../../../../../services/level';
import { ILevels } from '../../../../../interfaces/level';
import { IAcademicYears } from '../../../../../interfaces/academic-year';
import { AcademicYearService } from '../../../../../services/academic-year';

@Component({
  selector: 'app-class-overview',
  imports: [
    ActionButton,
    LoadingIndicator,
    NotFoundError,
    Pagination,
    NgClass,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './class-overview.html',
  styleUrl: './class-overview.css',
})
export class ClassOverview {
  constructor(
    private classService: ClassService,
    private router: Router,
    private route: ActivatedRoute,
    private schoolService: SchoolService,
    private session: Session,
    private levelService: LevelService,
    protected academicYearService: AcademicYearService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole === 'SUPER_ADMIN') {
      this.getSchools();
    }

    this.getClasses();
    this.getLevels();
    this.getAcademicYears();
  }

  protected currentFilterType = '';
  protected currentFilterValue = '';
  protected pagination = signal({ page: 0, size: 10 });
  protected readonly flashService = inject(FlashService);
  protected isFiltering = signal<boolean>(false);
  protected classes = signal<IClasses | null>(null);
  protected teachers = signal<ITeachers | null>(null);
  protected readonly Helpers = Helpers;
  protected loggedUserRole?: string;
  protected schoolId?: string;
  protected schools = signal<ISchools | null>(null);
  protected filterSchoolId: string = '';
  protected searchClasses: string = '';
  protected classStatus: string = '';
  protected levels = signal<ILevels | null>(null);
  protected academicYearId: string = '';
  protected academicYears = signal<IAcademicYears | null>(null);
  protected changeStatus = signal<boolean>(false);
  protected newStatus: string = '';
  protected validStatuses: string[] = ['ACTIVE', 'INACTIVE', 'ARCHIVED'];
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected isEditing = signal<boolean>(false);
  protected selectedClass: IClass['payload'] = {
    id: '',
    public_id: '',
    school_id: '',
    school_name: '',
    academic_year_id: '',
    academic_year_name: '',
    level_id: '',
    level_name: '',
    name: '',
    class_teacher_id: '',
    class_teacher_first_name: '',
    class_teacher_last_name: '',
    class_teacher_staff_number: '',
    capacity: '',
    status: '',
    created_at: '',
    updated_at: '',
  };

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };

    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Could not load Academic Years. Refresh the page.');
        },
      });
  }

  protected getClasses(filterType = this.currentFilterType, value = this.currentFilterValue): void {
    this.isFiltering.set(true);

    this.currentFilterType = filterType;
    this.currentFilterValue = value;

    let { page, size } = this.pagination();

    const params: Record<string, string | number> = {
      page,
      size,
    };

    if (filterType && value) {
      params[filterType] = value;
    }

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        this.classes.set(res);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load classes. Refresh the page');
      },
    });
  }

  protected loadPage(page: number): void {
    this.pagination.update((p) => ({
      ...p,
      page,
    }));

    this.getClasses();
  }

  protected getLevels(): void {
    this.isFiltering.set(true);

    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
    };

    this.levelService.getLevels(`${ApiEndpoints.url}/level`, params).subscribe({
      next: (res) => {
        this.levels.set(res);
        this.isFiltering.set(false);
      },
      error: (err) => {
        this.isFiltering.set(false);
        this.flashService.failure(err?.error?.message ?? 'Could not load levels. Refresh the page');
      },
    });
  }

  protected getSchools(): void {
    this.schoolService.getSchools(`${ApiEndpoints.url}/school`).subscribe({
      next: (data) => {
        this.schools.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Error loading schools.');
      },
    });
  }

  protected selectClass(action: string, payload: IClass['payload']): void {
    this.selectedClass = payload;

    if (action === 'status') {
      this.changeStatus.set(true);
    }
  }

  protected editClassFn(form: NgForm, action: 'status'): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    let payload: Record<string, any> = {};

    if (action === 'status') {
      payload = {
        status: this.newStatus,
      };
    }

    this.isEditing.set(true);

    this.classService
      .editClass(`${ApiEndpoints.url}/class/${this.selectedClass.id}`, payload)
      .subscribe({
        next: () => {
          this.isEditing.set(false);
          this.newStatus = '';
          this.changeStatus.set(false);
          this.flashService.success('Successfully updated class');
          this.getClasses();
        },
        error: (err) => {
          this.isEditing.set(false);
          this.flashService.failure(err?.error?.message ?? 'Error updating class');
        },
      });
  }

  protected navigateToDetailPage(id: string, url: string): void {
    this.router.navigate([`../${url}`], {
      relativeTo: this.route,
      queryParams: {
        id: id,
      },
    });
  }
}
